/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { Panel, PanelType, Stack, Text } from "@fluentui/react";
import React from "react";
import {
    IFieldDefinitionPanelSectionHeadingProps,
    IFieldDefinitionPanelSectionHeadingState
} from ".";
import { HuxTextBox } from "@nuance/hux-components";
import AccessLevelSelection from "./AccessLevelSelection";
import { FieldDefinitionPanelSectionHeadingCommands } from "./FieldDefinitionPanelSectionHeadingViewModel";
import { panelFooterProps, panelProps } from "./FieldDefinitionPanelStackTokens";
import { IPanelFooterProps } from "./IPanelFooterProps";
import PanelFooter from "./PanelFooter";

const fieldDefinitionPanelSectionHeadingCommands = new FieldDefinitionPanelSectionHeadingCommands();
const cmds = fieldDefinitionPanelSectionHeadingCommands.Cmds;

/**
 * Section heading panel for field definition
 *
 * @param props Properties for section heading panel
 * @returns Section heading panel
 */
const FieldDefinitionPanelSectionHeading = (
    props: IFieldDefinitionPanelSectionHeadingProps
): JSX.Element => {
    const initialState: IFieldDefinitionPanelSectionHeadingState = {
        common: { ...props.common },
        data: { ...props.data },
        errors: {},
        touched: {},
        isValid: !props.common.isNew,
        isDirty: false
    };

    const [state, dispatch] = React.useReducer(
        fieldDefinitionPanelSectionHeadingCommands.Reducer,
        initialState
    );

    const onSaveFieldDefinition = () => {
        // save the thing...
        if (props.common.onSaveContent !== undefined) {
            props.common.onSaveContent(fieldDefinitionPanelSectionHeadingCommands.Save(state));
        }
    };

    const footerProps: IPanelFooterProps = {
        onSave: onSaveFieldDefinition,
        onCancel: props.common.onClosePanel,
        isNew: state.common.isNew,
        isDirty: state.isDirty,
        isValid: state.isValid,
        panelFooterProps
    };

    return (
        <Panel
            type={PanelType.medium}
            isOpen={props.common.isOpen}
            onDismiss={props.common.onClosePanel}
            headerText={props.common.panelTitle}
            closeButtonAriaLabel="Close"
            onRenderFooterContent={() => {
                return PanelFooter(footerProps);
            }}
            // Stretch panel content to fill the available height so the footer is positioned
            // at the bottom of the page
            isFooterAtBottom={true}
        >
            <Stack {...panelProps}>
                <Text block>
                    Sections collect settings into an expand/collapse grouping. If a section is
                    added, all settings must be within a section.
                </Text>
                <Text block>
                    Use sections to help organize and display products with many settings.
                </Text>
                <HuxTextBox
                    required
                    label="Section Id"
                    name="sectionHeadingId"
                    value={state.data.sectionHeadingId}
                    errorMessage={
                        state.touched.sectionHeadingId ? state.errors.sectionHeadingId : ""
                    }
                    onChange={(e, value) =>
                        dispatch(
                            cmds.onTextChanged({
                                name: e.currentTarget.name,
                                value
                            })
                        )
                    }
                    onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
                    disabled={!props.common.isNew}
                />

                <HuxTextBox
                    required
                    label="Section label"
                    name="heading"
                    value={state.data.heading}
                    errorMessage={state.touched.heading ? state.errors.heading : ""}
                    onChange={(e, value) =>
                        dispatch(
                            cmds.onTextChanged({
                                name: e.currentTarget.name,
                                value
                            })
                        )
                    }
                    onBlur={e => dispatch(cmds.onBlur(e.currentTarget.name))}
                />

                <AccessLevelSelection
                    accessLevel={state.data.accessLevels}
                    onAccessLevelChange={accessLevel =>
                        dispatch(cmds.onAccessLevelChanged(accessLevel))
                    }
                />
            </Stack>
        </Panel>
    );
};

const SectionHeadingPanel = FieldDefinitionPanelSectionHeading;
export { SectionHeadingPanel };
